import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { motion, AnimatePresence } from 'framer-motion';

// Page imports
import { ChallengeDetailPage, LogActivityPage } from './pages/ChallengesPages';
import { CreateChallengePage } from './pages/CreateChallengePage';
import { CreatePostPage } from './pages/CreatePostPage';
import { EditPostPage } from './pages/EditPostPage';
import OrganizationPage from './pages/OrganizationPage';
import EditOrgPage from './pages/EditOrgPage';
import AllActionsPage from './pages/AllActionsPage';
import { ImpactPage, ProfilePage } from './pages/ImpactPages';
import { HabitsPage, HabitDetailPage } from './pages/HabitsPage';
import { MenuPage, NotificationsPage, ConnectedDevicesPage, RewardsPage, RedemptionHistoryPage } from './pages/MenuPage';
import LoginPage from './pages/LoginPage';
import DemoRegisterPage from './pages/DemoRegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import StepTrackingPage from './pages/StepTrackingPage';
import HRAdminPage from './pages/HRAdminPage';
import HomePage from './pages/HomePage';
import PostDetailPage from './pages/PostDetailPage';

// Community page imports
import CommunityLoginPage from './pages/CommunityLoginPage';
import CommunityRegisterOrganiserPage from './pages/CommunityRegisterOrganiserPage';
import CommunityHomePage from './pages/CommunityHomePage';
import CommunityPostDetailPage from './pages/CommunityPostDetailPage';
import CommunityAdminPage from './pages/CommunityAdminPage';
import CommunityCreatePostPage from './pages/CommunityCreatePostPage';
import CommunityCreateEventPage from './pages/CommunityCreateEventPage';
import CommunityCreateChallengePage from './pages/CommunityCreateChallengePage';
import CommunityCreateActionPage from './pages/CommunityCreateActionPage';
import CommunityAllEventsPage from './pages/CommunityAllEventsPage';
import CommunityChallengeDetailPage from './pages/CommunityChallengeDetailPage';
import CommunityEventDetailPage from './pages/CommunityEventDetailPage';
import CommunityProfilePage from './pages/CommunityProfilePage';
import CommunityOnboarding from './components/community/CommunityOnboarding';
import CommunityImpactPage from './pages/CommunityImpactPage';
import CommunityMenuPage, { CommunityNotificationsPage, CommunityRewardsPage } from './pages/CommunityMenuPage';

// Superadmin page imports
import SuperadminLoginPage from './pages/SuperadminLoginPage';
import SuperadminDashboardPage from './pages/SuperadminDashboardPage';
import { SuperadminAuthProvider, useSuperadminAuth } from './context/SuperadminAuthContext';

// Component imports
import InteractiveTour from './components/InteractiveTour';
import OnboardingQuest from './components/onboarding/OnboardingQuest';
import './components/onboarding/Onboarding.css';
import BottomNav from './components/common/BottomNav';
import TopHeader from './components/common/TopHeader';
import ProtectedRoute from './components/common/ProtectedRoute';
import RedirectIfAuthed from './components/common/RedirectIfAuthed';
import ManifestSwitcher from './components/common/ManifestSwitcher';
import { ChallengeCard, ProgressPlant } from './components/cards';
import { Toaster } from './components/ui/sonner';
import { ConfirmHost } from './components/ui/confirm-dialog';
import { installAlertOverride } from './utils/notify';

// Route legacy `window.alert(...)` calls (and any third-party libraries
// that use them) through the in-app sonner toast instead of the native
// browser dialog. Idempotent — safe to call on every module reload.
installAlertOverride();

// Shared context and utilities
import { AuthProvider, useAuth } from './context/AuthContext';
import { PointsProvider, usePoints } from './context/PointsContext';
import { CommunityAuthProvider, useCommunityAuth } from './context/CommunityAuthContext';
import { CommunityLangProvider } from './context/CommunityLangContext';

// ── Superadmin Route Guard ───────────────────────────────────
const SuperadminProtectedRoute = ({ children }) => {
  const { token, loading } = useSuperadminAuth();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0F172A', color: '#94A3B8' }}>Loading…</div>;
  if (!token) return <Navigate to="/superadmin/login" replace />;
  return children;
};

const SuperadminApp = () => (
  <Routes>
    <Route path="login" element={<SuperadminLoginPage />} />
    <Route path="*" element={<SuperadminProtectedRoute><SuperadminDashboardPage /></SuperadminProtectedRoute>} />
  </Routes>
);

// Re-export for backward compat
export { useAuth, usePoints };

// ── Community Route Guard ────────────────────────────────────
const CommunityProtectedRoute = ({ children }) => {
  const { token, loading } = useCommunityAuth();
  const location = useLocation();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>Loading...</div>;
  if (!token) {
    // Remember the page the user was trying to reach so the login page
    // can send them back there after sign-in (e.g. a shared
    // /community/posts/:id URL opened in incognito).
    return <Navigate to="/community/login" replace state={{ from: location }} />;
  }
  return children;
};

// ── Community App (with onboarding) ──────────────────────────
const CommunityApp = () => {
  const { user, token, loading, refreshUser } = useCommunityAuth();
  const [onboarded, setOnboarded] = useState(false);

  useEffect(() => {
    if (token && user) {
      if (user.role === 'COMMUNITY_ADMIN' || user.onboardingData?.completed === true) {
        setOnboarded(true);
      } else {
        setOnboarded(false);
      }
    }
  }, [token, user]);

  const handleOnboardingComplete = async (data) => {
    try {
      const axios = (await import('axios')).default;
      const { API } = await import('./utils/api');
      await axios.patch(`${API}/community/users/me/onboarding`, { ...data, completed: true }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Re-fetch /users/me so the top header's star count picks up the
      // 50-star onboarding award immediately instead of waiting for a reload.
      if (refreshUser) await refreshUser();
    } catch {}
    setOnboarded(true);
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>Loading...</div>;

  return (
    <Routes>
      <Route path="login" element={<CommunityLoginPage />} />
      {/* Public route — anyone (logged in or not) can apply to be an organiser. */}
      <Route path="register-organiser" element={<CommunityRegisterOrganiserPage />} />
      {/* Public route — community forgot password. */}
      <Route path="forgot-password" element={<ForgotPasswordPage scope="community" />} />
      <Route path="*" element={
        <CommunityProtectedRoute>
          {!onboarded ? (
            <CommunityOnboarding onComplete={handleOnboardingComplete} />
          ) : (
            <Routes>
              <Route path="/" element={<CommunityHomePage />} />
              <Route path="admin" element={<CommunityAdminPage />} />
              <Route path="create-post" element={<CommunityCreatePostPage />} />
              <Route path="create-event" element={<CommunityCreateEventPage />} />
              <Route path="create-challenge" element={<CommunityCreateChallengePage />} />
              <Route path="create-action" element={<CommunityCreateActionPage />} />
              <Route path="events" element={<CommunityAllEventsPage />} />
              <Route path="challenges/:challengeId" element={<CommunityChallengeDetailPage />} />
              <Route path="event-detail/:eventId" element={<CommunityEventDetailPage />} />
              <Route path="posts/:postId" element={<CommunityPostDetailPage />} />
              <Route path="profile" element={<CommunityProfilePage />} />
              <Route path="impact" element={<CommunityImpactPage />} />
              <Route path="leaderboard" element={<CommunityImpactPage />} />
              <Route path="menu" element={<CommunityMenuPage />} />
              <Route path="rewards" element={<CommunityRewardsPage />} />
              <Route path="notifications" element={<CommunityNotificationsPage />} />
            </Routes>
          )}
        </CommunityProtectedRoute>
      } />
    </Routes>
  );
};

// ── Org AuthenticatedApp (unchanged) ─────────────────────────
const AuthenticatedApp = ({ showTour, setShowTour }) => {
  const navigate = useNavigate();
  const { token, loading, user, refreshUser } = useAuth();
  const { refreshPoints } = usePoints();
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(true);
  const tourBootstrappedRef = useRef(false);
  const isHRAdmin = user?.role === 'HR_ADMIN';

  useEffect(() => {
    if (!token) {
      setOnboardingLoading(false);
      return;
    }
    if (isHRAdmin) {
      setHasCompletedOnboarding(true);
      setOnboardingLoading(false);
      return;
    }
    // Wait until /users/me has resolved before deciding
    if (!user) return;
    // Server flag is the source of truth — covers both the welcome quiz
    // (OnboardingQuest) and the InteractiveTour. Once true, neither shows again.
    if (user.has_completed_onboarding) {
      setHasCompletedOnboarding(true);
    }
    setOnboardingLoading(false);

    // Auto-trigger the app tour ONCE for first-time users (server-side flag).
    // Use a ref guard so React StrictMode (dev double-mount) doesn't re-schedule.
    // Intentionally NO cleanup that clears the timer — the ref guarantees we
    // schedule it exactly once across the component's lifetime.
    if (!user.has_completed_onboarding && !showTour && !tourBootstrappedRef.current) {
      tourBootstrappedRef.current = true;
      setTimeout(() => setShowTour(true), 1500);
    }
  }, [token, user, showTour, setShowTour, isHRAdmin]);

  const completeTourOnServer = useCallback(async (awardStars, opts = {}) => {
    if (!token) return null;
    // deferUserRefresh: skip refreshUser() so the outer effect at
    // App.js#189-209 doesn't observe user.has_completed_onboarding=true
    // and unmount OnboardingQuest before the celebration screen renders.
    // The onboarding-complete path uses this and fires refreshUser()
    // manually from handleOnboardingDismiss after the user dismisses
    // the celebration. refreshPoints() is safe to call either way — it
    // only updates PointsContext state, never AuthContext.user.
    const { deferUserRefresh = false } = opts;
    try {
      const axios = (await import('axios')).default;
      const { API } = await import('./utils/api');
      const res = await axios.post(
        `${API}/users/me/complete-tour`,
        { award_stars: awardStars },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!deferUserRefresh && refreshUser) await refreshUser();
      if (refreshPoints) refreshPoints();
      return res.data;
    } catch (err) {
      console.error('Failed to complete tour on server:', err);
      return null;
    }
  }, [token, refreshUser, refreshPoints]);

  // Called by OnboardingQuest on step-5 (celebration) mount. Posts to
  // the idempotent /users/me/complete-tour and returns the server-
  // confirmed award. Does NOT flip hasCompletedOnboarding — that would
  // unmount OnboardingQuest before the celebration screen renders.
  // Uses deferUserRefresh so refreshUser() also can't retroactively
  // trigger the outer effect and unmount the celebration mid-render.
  const handleOnboardingComplete = useCallback(async (data) => {
    localStorage.setItem('socialstar_onboarding', JSON.stringify(data));
    const result = await completeTourOnServer(true, { deferUserRefresh: true });
    return {
      stars_awarded: result?.stars_awarded ?? 0,
      server_ok: !!result,
    };
  }, [completeTourOnServer]);

  // Called by OnboardingQuest when the user taps the celebration screen's
  // CTA. Drops the onboarding overlay AND fires the deferred refreshUser
  // so social_score + has_completed_onboarding sync into AuthContext now
  // that the celebration has actually been seen and dismissed.
  const handleOnboardingDismiss = useCallback(() => {
    setHasCompletedOnboarding(true);
    if (refreshUser) refreshUser();
  }, [refreshUser]);

  const handleTourComplete = useCallback(async () => {
    setShowTour(false);
    const result = await completeTourOnServer(true);
    if (result?.stars_awarded > 0) {
      try {
        const { toast } = await import('sonner');
        toast.success(`🌟 You earned ${result.stars_awarded} stars for completing the tour!`);
      } catch {}
    }
  }, [setShowTour, completeTourOnServer]);

  const handleTourSkip = useCallback(async () => {
    setShowTour(false);
    await completeTourOnServer(false);
  }, [setShowTour, completeTourOnServer]);

  useEffect(() => {
    if (showTour && window.location.pathname !== '/') navigate('/');
  }, [showTour, navigate]);

  if (loading || onboardingLoading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh',
      background: 'linear-gradient(135deg, #F8FAFC 0%, #E0F2F1 30%, #F8FAFC 100%)' }}>
      <div style={{ fontSize: 48 }}>&#11088;</div>
    </div>;
  }

  if (token && !hasCompletedOnboarding) return <OnboardingQuest onComplete={handleOnboardingComplete} onDismiss={handleOnboardingDismiss} />;

  return (
    <>
      <AnimatePresence>
        {showTour && <InteractiveTour onComplete={handleTourComplete} onSkip={handleTourSkip} />}
      </AnimatePresence>
      <Routes>
        <Route path="/login" element={<RedirectIfAuthed><LoginPage /></RedirectIfAuthed>} />
        <Route path="/demo-register" element={<DemoRegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage scope="org" />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/" element={<ProtectedRoute><HomePage setShowTour={setShowTour} /></ProtectedRoute>} />
        <Route path="/all-actions" element={<ProtectedRoute><AllActionsPage /></ProtectedRoute>} />
        <Route path="/challenges/:id" element={<ProtectedRoute><ChallengeDetailPage BottomNav={BottomNav} TopHeader={TopHeader} ProgressPlant={ProgressPlant} useAuth={useAuth} /></ProtectedRoute>} />
        <Route path="/log-activity" element={<ProtectedRoute><LogActivityPage BottomNav={BottomNav} TopHeader={TopHeader} /></ProtectedRoute>} />
        <Route path="/create" element={<Navigate to="/create-challenge" replace />} />
        <Route path="/challenges/:id/edit" element={<ProtectedRoute><CreateChallengePage BottomNav={BottomNav} TopHeader={TopHeader} /></ProtectedRoute>} />
        <Route path="/create-challenge" element={<ProtectedRoute><CreateChallengePage BottomNav={BottomNav} TopHeader={TopHeader} /></ProtectedRoute>} />
        <Route path="/create-post" element={<ProtectedRoute><CreatePostPage BottomNav={BottomNav} TopHeader={TopHeader} useAuth={useAuth} /></ProtectedRoute>} />
        <Route path="/edit-post/:postId" element={<ProtectedRoute><EditPostPage BottomNav={BottomNav} TopHeader={TopHeader} useAuth={useAuth} /></ProtectedRoute>} />
        <Route path="/posts/:postId" element={<ProtectedRoute><PostDetailPage /></ProtectedRoute>} />
        <Route path="/habits" element={<ProtectedRoute><HabitsPage BottomNav={BottomNav} TopHeader={TopHeader} useAuth={useAuth} /></ProtectedRoute>} />
        <Route path="/habits/:id" element={<ProtectedRoute><HabitDetailPage BottomNav={BottomNav} TopHeader={TopHeader} /></ProtectedRoute>} />
        <Route path="/impact" element={<ProtectedRoute><ImpactPage BottomNav={BottomNav} TopHeader={TopHeader} useAuth={useAuth} /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage BottomNav={BottomNav} TopHeader={TopHeader} useAuth={useAuth} /></ProtectedRoute>} />
        <Route path="/menu" element={<ProtectedRoute><MenuPage BottomNav={BottomNav} TopHeader={TopHeader} setShowTour={setShowTour} /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage BottomNav={BottomNav} TopHeader={TopHeader} /></ProtectedRoute>} />
        <Route path="/devices" element={<ProtectedRoute><ConnectedDevicesPage BottomNav={BottomNav} TopHeader={TopHeader} /></ProtectedRoute>} />
        <Route path="/rewards" element={<ProtectedRoute><RewardsPage BottomNav={BottomNav} TopHeader={TopHeader} useAuth={useAuth} /></ProtectedRoute>} />
        <Route path="/rewards/history" element={<ProtectedRoute><RedemptionHistoryPage BottomNav={BottomNav} TopHeader={TopHeader} /></ProtectedRoute>} />
        <Route path="/organization" element={<ProtectedRoute><OrganizationPage BottomNav={BottomNav} /></ProtectedRoute>} />
        <Route path="/organization/edit" element={<ProtectedRoute><EditOrgPage /></ProtectedRoute>} />
        <Route path="/steps" element={<ProtectedRoute><StepTrackingPage BottomNav={BottomNav} TopHeader={TopHeader} /></ProtectedRoute>} />
        <Route path="/hr-admin" element={<ProtectedRoute><HRAdminPage BottomNav={BottomNav} TopHeader={TopHeader} /></ProtectedRoute>} />
      </Routes>
    </>
  );
};

// ── Root App ─────────────────────────────────────────────────
function App() {
  const [showTour, setShowTour] = useState(false);

  return (
    <BrowserRouter>
      <ManifestSwitcher />
      <Toaster position="top-center" richColors />
      <ConfirmHost />
      <Routes>
        {/* Superadmin routes — fully isolated auth context */}
        <Route path="/superadmin/*" element={
          <SuperadminAuthProvider>
            <SuperadminApp />
          </SuperadminAuthProvider>
        } />
        {/* Community routes — separate auth context */}
        <Route path="/community/*" element={
          <CommunityAuthProvider>
            <CommunityLangProvider>
              <CommunityApp />
            </CommunityLangProvider>
          </CommunityAuthProvider>
        } />
        {/* Org routes — existing auth context */}
        <Route path="/*" element={
          <AuthProvider>
            <PointsProvider>
              <AuthenticatedApp showTour={showTour} setShowTour={setShowTour} />
            </PointsProvider>
          </AuthProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
